import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ProfilePage from "./ProfilePage";
import PastBookingsPage from "./PastBookingsPage";
import UserBookingsPage from "./UserBookingsPage";

export default function AccountExperiencePage() {
  const [searchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get("section") || "profile";
  const [activeSubpage, setActiveSubpage] = useState(sectionFromUrl);

  // Update activeSubpage when URL changes
  useEffect(() => {
    const section = searchParams.get("section") || "profile";
    setActiveSubpage(section);
  }, [searchParams]);

  function linkClasses(type: string | null = null) {
    let classes =
      "w-full flex items-center gap-4 p-4 rounded-2xl transition-colors text-left cursor-pointer button-hover";
    if (
      type === activeSubpage ||
      (activeSubpage === undefined && type === "profile")
    ) {
      classes += " bg-[var(--secondary)] text-gray-900";
    } else {
      classes += " hover:bg-gray-50 text-gray-700";
    }
    return classes;
  }

  return (
    <div className="flex">
      {/* Left Navigation - 1/6 width */}
      <div className="w-1/6 min-w-64 bg-white border-r border-gray-200">
        <div className="p-6 sticky top-0">
          <nav className="space-y-2">
            {/* About me */}
            <button
              className={`${linkClasses("profile")} ${
                activeSubpage === undefined || activeSubpage === "profile"
                  ? "active"
                  : ""
              } group`}
              onClick={() => setActiveSubpage("profile")}
            >
              <div
                className={`w-12 h-12 min-w-12 min-h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activeSubpage === undefined || activeSubpage === "profile"
                    ? "bg-white text-black"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                  />
                </svg>
              </div>
              <span
                className={`font-medium ${
                  activeSubpage === undefined || activeSubpage === "profile"
                    ? "text-white"
                    : "text-black"
                }`}
              >
                About me
              </span>
            </button>

            {/* Upcoming Bookings */}
            <button
              className={`${linkClasses("upcomingBookings")} ${
                activeSubpage === "upcomingBookings" ? "active" : ""
              } group`}
              onClick={() => setActiveSubpage("upcomingBookings")}
            >
              <div
                className={`w-12 h-12 min-w-12 min-h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activeSubpage === "upcomingBookings"
                    ? "bg-white text-black"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5M9 12.75h6m-6 3h6"
                  />
                </svg>
              </div>
              <span
                className={`font-medium ${
                  activeSubpage === undefined ||
                  activeSubpage === "upcomingBookings"
                    ? "text-white"
                    : "text-black"
                }`}
              >
                Upcoming Bookings
              </span>
            </button>

            {/* Past Bookings */}
            <button
              className={`${linkClasses("pastBookings")} ${
                activeSubpage === "pastBookings" ? "active" : ""
              } group`}
              onClick={() => setActiveSubpage("pastBookings")}
            >
              <div
                className={`w-12 h-12 min-w-12 min-h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activeSubpage === "pastBookings"
                    ? "bg-white text-black"
                    : "bg-gray-200 text-gray-600"
                }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                  />
                </svg>
              </div>
              <span
                className={`font-medium ${
                  activeSubpage === undefined ||
                  activeSubpage === "pastBookings"
                    ? "text-white"
                    : "text-black"
                }`}
              >
                Past Bookings
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Right Content Area - 5/6 width */}
      <div className="flex-1">
        <div className="min-h-screen">
          {activeSubpage === "profile" && <ProfilePage />}
          {activeSubpage === "upcomingBookings" && <UserBookingsPage />}
          {activeSubpage === "pastBookings" && <PastBookingsPage />}
        </div>
      </div>
    </div>
  );
}
